import React, { useEffect, useState } from 'react';
import ClanLevelBadge from './clan-level-badge';
import { ArenaClanQuarterRow, getArenaQuarters, getClanQuarterLeaderboard } from '../../../api/group/arena.api';

// "2026-Q4" thành "Quý 4/2026".
export const quarterLabel = (q: string): string => {
  const m = /^(\d{4})-Q([1-4])$/.exec(q);
  return m ? `Quý ${m[2]}/${m[1]}` : q;
};

const ClanQuarterLeaderboard: React.FC = () => {
  const [quarters, setQuarters] = useState<string[]>([]);
  const [quarter, setQuarter] = useState('');
  const [rows, setRows] = useState<ArenaClanQuarterRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getArenaQuarters()
      .then((qs) => {
        const list = [...(qs || [])].sort().reverse();
        setQuarters(list);
        if (list.length > 0) setQuarter(list[0]);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!quarter) return;
    let cancelled = false;
    setLoading(true);
    getClanQuarterLeaderboard(quarter)
      .then((r) => !cancelled && setRows(r || []))
      .catch(() => !cancelled && setRows([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [quarter]);

  if (quarters.length === 0 && !loading) return null;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[24px] font-semibold tracking-tight text-[#19052f] dark:text-white">Bảng xếp hạng quý</h2>
        <select
          data-testid="quarter-select"
          className="rounded-lg border border-[#DEE0E6] bg-white px-3 py-2 text-[14px] outline-none focus:border-[#066bff] dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          value={quarter}
          onChange={(e) => setQuarter(e.target.value)}
        >
          {quarters.map((q) => (
            <option key={q} value={q}>
              {quarterLabel(q)}
            </option>
          ))}
        </select>
      </div>
      <div className="overflow-hidden rounded-[20px] bg-white shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-dark2">
        <div className="grid grid-cols-[52px_1fr_110px_96px] gap-2 border-b border-[#eef1f5] px-5 py-3 text-[13px] font-medium text-[#657384] dark:border-slate-700 dark:text-slate-400">
          <span>Hạng</span>
          <span>Clan</span>
          <span className="text-right">Điểm quý</span>
          <span className="text-right">Số lần Top 1</span>
        </div>
        {loading ? (
          <div className="px-5 py-6 text-center text-[14px] text-[#657384] dark:text-slate-400">Đang tải…</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-6 text-center text-[14px] text-[#657384] dark:text-slate-400">Quý này chưa có clan nào có điểm.</div>
        ) : (
          rows.map((r) => (
            <div
              key={r.clanId}
              className="grid grid-cols-[52px_1fr_110px_96px] items-center gap-2 border-b border-[#f4f6f9] px-5 py-3 text-[14px] last:border-b-0 dark:border-slate-700/60"
            >
              <span className="text-[16px] font-bold text-[#066bff]">#{r.rank}</span>
              <span className="flex min-w-0 items-center gap-2 font-medium text-[#19052f] dark:text-white">
                <span className="inline-block size-3 shrink-0 rounded-full" style={{ background: r.crestColor || '#066bff' }} />
                <span className="truncate">{r.name}</span>
                <ClanLevelBadge level={r.level} />
              </span>
              <span className="text-right font-semibold text-[#19052f] dark:text-white">{Number(r.points).toLocaleString('vi-VN')}</span>
              <span className="text-right text-[#657384] dark:text-slate-400">{r.rank1Count.toLocaleString('vi-VN')}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default ClanQuarterLeaderboard;
